import React, { useState } from 'react';
import axios from '../../axios';
import { IWorkbookSource } from '../../interfaces/domain';
import Button, { EButtonSize } from '../../components/UI/Button/Button';

import classes from './WorkbookSourcesPanel.module.css';

type TabType = 'text' | 'url' | 'file';

const SOURCE_ICONS: Record<string, string> = {
  TEXT: '📝',
  URL: '🔗',
  FILE: '📎',
};

const STATUS_BADGES: Record<string, { className: string; label: string }> = {
  READY: { className: classes.badgeReady, label: '✅ Valmis' },
  FETCHING: { className: classes.badgeFetching, label: '⏳ Haetaan' },
  FAILED: { className: classes.badgeFailed, label: '❌ Epäonnistui' },
};

const ACCEPTED_EXTENSIONS = '.txt,.md,.csv,.pdf';

/** Palauttaa backend-serverin base-URL:n.
 *  Kehityksessä React (3001) ja Java-backend (8080) pyörivät eri porteissa,
 *  joten suorat linkit pitää osoittaa oikeaan porttiin.
 */
const getBackendBase = (): string => {
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:8080';
  }
  return '';
};

interface IProps {
  taskId: string;
  sources: IWorkbookSource[];
  onSourcesChanged: () => void;
}

const formatBytes = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const WorkbookSourcesPanel: React.FC<IProps> = ({ taskId, sources, onSourcesChanged }) => {
  const [activeTab, setActiveTab] = useState<TabType>('text');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Text form
  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');

  // URL form
  const [urlValue, setUrlValue] = useState('');
  const [urlTitle, setUrlTitle] = useState('');

  // File form
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const totalChars = sources.reduce((sum, s) => sum + (s.contentLength || 0), 0);

  const handleAddTextSource = async () => {
    if (!textTitle.trim() || !textContent.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await axios.post('/json/market-research', {
        action: 'add-source-text',
        taskId,
        title: textTitle,
        content: textContent,
      });
      setTextTitle('');
      setTextContent('');
      onSourcesChanged();
    } catch (err: any) {
      setError(err.response?.data || 'Virhe lisättäessä lähdettä');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUrlSource = async () => {
    if (!urlValue.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await axios.post('/json/market-research', {
        action: 'add-source-url',
        taskId,
        url: urlValue,
        title: urlTitle || undefined,
      });
      setUrlValue('');
      setUrlTitle('');
      onSourcesChanged();
    } catch (err: any) {
      setError(err.response?.data || 'Virhe lisättäessä URL-lähdettä');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFileSource = async () => {
    if (!selectedFile) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('action', 'add-source-file');
      formData.append('taskId', taskId);
      formData.append('file', selectedFile);

      await axios.post('/json/market-research', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById('workbook-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      onSourcesChanged();
    } catch (err: any) {
      setError(err.response?.data || 'Virhe ladattaessa tiedostoa');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSource = async (sourceId: string) => {
    if (!window.confirm('Haluatko varmasti poistaa tämän lähteen?')) return;
    try {
      await axios.post('/json/market-research', {
        action: 'delete-source',
        id: parseInt(sourceId),
      });
      onSourcesChanged();
    } catch (err: any) {
      setError(err.response?.data || 'Virhe poistettaessa lähdettä');
    }
  };

  const handleDownloadFile = async (source: IWorkbookSource) => {
    try {
      const response = await axios.get(
        `${getBackendBase()}/json/market-research/download?id=${source.id}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], {
        type: (response.headers as any)['content-type'] || 'application/octet-stream',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = source.fileName || source.title || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Tiedoston lataus epäonnistui: ' + (err?.response?.data || err?.message || 'Tuntematon virhe'));
    }
  };

  return (
    <div className={classes.panel}>
      <h3 className={classes.title}>
        📓 Lähdeaineisto
        <span className={classes.summary}>
          {sources.length} lähdettä · {totalChars.toLocaleString('fi-FI')} merkkiä
        </span>
      </h3>

      {error && <div style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: 12 }}>{error}</div>}

      {/* Tabs */}
      <div className={classes.tabs}>
        <button
          className={activeTab === 'text' ? classes.tabActive : classes.tab}
          onClick={() => setActiveTab('text')}
        >
          📝 Teksti
        </button>
        <button
          className={activeTab === 'url' ? classes.tabActive : classes.tab}
          onClick={() => setActiveTab('url')}
        >
          🔗 URL
        </button>
        <button
          className={activeTab === 'file' ? classes.tabActive : classes.tab}
          onClick={() => setActiveTab('file')}
        >
          📎 Tiedosto
        </button>
      </div>

      {/* Text tab */}
      {activeTab === 'text' && (
        <div className={classes.addForm}>
          <div className={classes.field}>
            <label>Otsikko</label>
            <input
              type="text"
              value={textTitle}
              onChange={(e) => setTextTitle(e.target.value)}
              placeholder="Esim. Kilpailija-analyysi"
              className={classes.input}
              disabled={loading}
            />
          </div>
          <div className={classes.field}>
            <label>Sisältö (markdown / teksti)</label>
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Liitä tähän raportin teksti tai markdown-sisältö..."
              className={classes.textarea}
              rows={5}
              disabled={loading}
            />
          </div>
          <div className={classes.addActions}>
            <Button
              size={EButtonSize.SMALL}
              onClick={handleAddTextSource}
              disabled={loading || !textTitle.trim() || !textContent.trim()}
            >
              {loading ? 'Lisätään...' : '+ Lisää tekstilähde'}
            </Button>
          </div>
        </div>
      )}

      {/* URL tab */}
      {activeTab === 'url' && (
        <div className={classes.addForm}>
          <div className={classes.field}>
            <label>URL-osoite</label>
            <input
              type="url"
              value={urlValue}
              onChange={(e) => setUrlValue(e.target.value)}
              placeholder="https://esimerkki.fi/raportti"
              className={classes.input}
              disabled={loading}
            />
          </div>
          <div className={classes.field}>
            <label>Otsikko (valinnainen, haetaan automaattisesti)</label>
            <input
              type="text"
              value={urlTitle}
              onChange={(e) => setUrlTitle(e.target.value)}
              placeholder="Jätä tyhjäksi niin käytetään sivun otsikkoa"
              className={classes.input}
              disabled={loading}
            />
          </div>
          <div className={classes.addActions}>
            <Button
              size={EButtonSize.SMALL}
              onClick={handleAddUrlSource}
              disabled={loading || !urlValue.trim()}
            >
              {loading ? 'Haetaan sisältöä...' : '+ Lisää URL-lähde'}
            </Button>
          </div>
        </div>
      )}

      {/* File tab */}
      {activeTab === 'file' && (
        <div className={classes.addForm}>
          <div className={classes.field}>
            <label>Tiedosto (.txt, .md, .csv, .pdf — max 10 MB)</label>
            <input
              id="workbook-file-input"
              type="file"
              accept={ACCEPTED_EXTENSIONS}
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              className={classes.fileInput}
              disabled={loading}
            />
          </div>
          <div className={classes.addActions}>
            <Button
              size={EButtonSize.SMALL}
              onClick={handleAddFileSource}
              disabled={loading || !selectedFile}
            >
              {loading ? 'Ladataan...' : '+ Lataa tiedosto'}
            </Button>
          </div>
        </div>
      )}

      {/* Source list */}
      <div className={classes.sourceList}>
        {sources.length === 0 ? (
          <p className={classes.emptyMessage}>
            Ei vielä lähteitä. Lisää aineistoa yllä olevista välilehdistä.
          </p>
        ) : (
          sources.map((source) => {
            const icon = SOURCE_ICONS[source.sourceType] || '📄';
            const statusBadge = STATUS_BADGES[source.status] || STATUS_BADGES.READY;

            return (
              <div key={source.id} className={classes.sourceCard}>
                <span className={classes.sourceIcon}>{icon}</span>
                <div className={classes.sourceInfo}>
                  <p className={classes.sourceTitle}>{source.title}</p>
                  {source.contentPreview && (
                    <p className={classes.sourcePreview}>{source.contentPreview}</p>
                  )}
                  <div className={classes.sourceMeta}>
                    <span className={statusBadge.className}>{statusBadge.label}</span>
                    {source.contentLength != null && source.contentLength > 0 && (
                      <span className={classes.badge}>{source.contentLength.toLocaleString('fi-FI')} merkkiä</span>
                    )}
                    {source.sourceType === 'FILE' && source.fileSize != null && (
                      <span className={classes.badge}>{formatBytes(source.fileSize)}</span>
                    )}
                    {source.sourceType === 'URL' && source.url && (
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.urlBadge}
                        title={source.url}
                      >
                        {(() => { try { return new URL(source.url).hostname; } catch { return source.url; } })()}
                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 3, verticalAlign: 'middle', flexShrink: 0 }}>
                          <path d="M7 1h4v4M11 1L5.5 6.5M5 2H2a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
                <div className={classes.sourceActions}>
                  {source.sourceType === 'FILE' && source.id && (
                    <button
                      onClick={() => handleDownloadFile(source)}
                      className={classes.iconBtn}
                      title="Lataa tiedosto"
                    >
                      📥
                    </button>
                  )}
                  <button
                    className={classes.iconBtnDanger}
                    onClick={() => handleDeleteSource(source.id)}
                    title="Poista lähde"
                  >
                    🗑
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default WorkbookSourcesPanel;
